import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthServiceInterface } from '../../../../services/auth/auth-service.interface';
import { IDataService } from '../../../../services/data/data-service.interface';
import { RbacPermissionsConstant } from '../../../../services/rbac/rbac-permissions.constant';
import { RbacServiceInterface } from '../../../../services/rbac/rbac-service.interface';
import { IFilterValues, ITableService } from '../../../../services/tables/interfaces/table.service.interface';
import { INotification, INotificationsApplicationService, INotificationTable } from './notifications-service.interface';

@Injectable()
export class NotificationsApplicationService implements INotificationsApplicationService {
  public notifications$: Observable<INotification[]>;
  private tableNotifications$: Subject<INotificationTable[]> = new Subject();
  public page$: BehaviorSubject<number> = new BehaviorSubject(1);
  private filters$: BehaviorSubject<IFilterValues> = new BehaviorSubject({});
  public totalPages = 0;
  public filterValues: IFilterValues;
  private slackLink: string;

  constructor(
    @Inject('ITableService') private tableService: ITableService,
    @Inject('AuthServiceInterface') public authService: AuthServiceInterface,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('RbacServiceInterface') private rbacService: RbacServiceInterface
  ) {
    const userId = this.authService.userData()?.id;
    const slack = this.dataService.getData()?.oauth?.slack;

    if (slack) {
      this.slackLink =
        'https://slack.com/oauth/v2/authorize?client_id=' +
        slack.client_id +
        '&scope=channels:read,chat:write,groups:read,im:read,mpim:read,channels:join,chat:write.public,team:read&user_scope=channels:read,groups:read,mpim:read,im:read,team:read' +
        '&redirect_uri=' +
        slack.redirect_uri +
        '&state=tracking-' +
        userId;
    }

    if (this.rbacService.can(RbacPermissionsConstant.PAGES_TABLE_NOTIFICATIONS)) {
      this.init().subscribe((data: any) => {
        this.tableNotifications$.next(data);
      });
    }
  }

  init() {
    const page$ = this.page$.asObservable();
    const filters$ = this.filters$.asObservable();

    return combineLatest([page$, filters$]).pipe(
      switchMap(([page, filters]) => {
        return this.getTableData(page, filters);
      })
    );
  }

  getTableData(page, filters) {
    return this.tableService.getData('NotificationsTable', page, 5, filters, {}).pipe(
      tap((data) => (this.totalPages = data.total)),
      map((data) => data.rows as INotification[])
    );
  }

  public getNotifications(): Observable<INotification[]> {
    return this.tableNotifications$.asObservable();
  }

  onChangePage(page: number): void {
    this.page$.next(page);
  }

  onFilterChange(filter: IFilterValues): void {
    this.filters$.next(filter);
  }

  getTotalPages(): number {
    return this.totalPages;
  }

  getSlackLink(): string {
    return this.slackLink;
  }
}
